import { useCallback } from 'react';
import { parsePath, useNavigate as useRouterNavigate, type NavigateOptions, type To } from 'react-router';

// Runtime SDK
import { buildUrl } from '@salesforce/storefront-next-runtime/site-context';

// Hooks
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useCurrentSiteAndLocaleRef } from '@/hooks/use-current-site-and-locale-ref';

/**
 * Site-context-aware `useNavigate`. An enhanced version of React Router's useNavigate hook.
 * Automatically applies URL prefix and search params from the app's URL config
 * to string destinations. Requires a SiteProvider to be mounted.
 *
 * - String `to` values with a pathname are transformed via `buildUrl`.
 * - Query/hash-only strings pass through unchanged to preserve the current route.
 * - Object `to` values with a `pathname` have the pathname transformed.
 * - Object `to` values without a `pathname` (e.g. `{ search }`) pass through unchanged.
 * - Numeric `to` values (history navigation like `navigate(-1)`) pass through unchanged.
 */
export function useNavigate() {
    const routerNavigate = useRouterNavigate();
    const config = useConfig();
    const { siteRef, localeRef } = useCurrentSiteAndLocaleRef();

    return useCallback(
        (to: To | number, options?: NavigateOptions) => {
            if (typeof to === 'number') {
                return routerNavigate(to);
            }

            const params = { siteId: siteRef, localeId: localeRef };

            if (typeof to === 'string') {
                return routerNavigate(
                    parsePath(to).pathname ? buildUrl({ to, urlConfig: config.url, params }) : to,
                    options
                );
            }

            // Object To — only transform if pathname is present
            if (to.pathname) {
                const pathname = buildUrl({ to: to.pathname, urlConfig: config.url, params });
                return routerNavigate({ ...to, pathname }, options);
            }

            return routerNavigate(to, options);
        },
        [routerNavigate, siteRef, localeRef, config.url]
    );
}
